import { inject, Injectable } from '@angular/core';
import { MaintenanceTask } from './maintenance/MaintenanceTask.model';
import { ApiService } from '../services/api.service';
import { MaintenanceTaskCompletionDTO } from './maintenance/MaintenanceTaskCompletionDTO.model';
import { MaintenanceTaskDTO } from './maintenance-task-dto';
import { UserService } from '../services/user.service';
import { MaintenanceTaskCategory } from './maintenance-task-category';
import { ShowService } from './show.service';

@Injectable({
    providedIn: 'root'
})
export class MaintenanceService {

    private readonly showService: ShowService = inject(ShowService);

    constructor(private api: ApiService, private userService: UserService) {
    }

    public async getAllMaintenanceTasks(top?: number): Promise<MaintenanceTask[]> {
        const showId = await this.showService.getActiveShowId();
        const endpoint = top ? `maintenance/maintenancetasks?showId=${ showId }&top=${ top }` : `maintenance/maintenancetasks?showId=${ showId }`;

        const data = await this.api.getAll<MaintenanceTask>(
            endpoint
        );
        const maintenanceTasks = data.map(taskData =>
            Object.assign(new MaintenanceTask(), taskData)
        );
        return this.sortMaintenanceTasks(maintenanceTasks);
    }

    public async getOverdueMaintenanceTasks(): Promise<MaintenanceTask[]> {
        const showId = await this.showService.getActiveShowId();

        const data = await this.api.getAll<MaintenanceTask>(
            `maintenance/maintenancetasks?showId=${ showId }`
        );
        const maintenanceTasks = data.map(taskData =>
            Object.assign(new MaintenanceTask(), taskData)
        );

        return this.sortMaintenanceTasks(maintenanceTasks).filter(
            task => task.daysUntilNextCompletion < 0
        );
    }

    /**
     * @param date optional ISO date (yyyy-MM-dd). When supplied, the backend
     *             returns only items completed on that day for the active show,
     *             which is much cheaper than fetching the full history and
     *             filtering client-side.
     */
    public async getAllMaintenanceTaskItems(date?: string): Promise<any[]> {
        const showId = await this.showService.getActiveShowId();
        const endpoint = date ? `maintenance/maintenancetaskitems?showId=${ showId }&date=${ date }` : `maintenance/maintenancetaskitems?showId=${ showId }`;
        return await this.api.getAll<any>(endpoint);
    }

    /**
     * Returns the raw completion-history rows for a single task (array-of-arrays,
     * newest first, one row per completion day). See `completion-cadence.ts` for
     * the row shape. The backend scopes this to the task itself, so no showId is
     * needed.
     */
    public async getTaskCompletionHistory(taskId: number): Promise<any[]> {
        // NB: this endpoint returns its history as a JSON-encoded STRING in
        // `data` (not a parsed array), so parse it here before use.
        const data: any = await this.api.getAll<any>(`maintenance/maintenancetasks/${ taskId }/history`);
        if (typeof data === 'string') {
            try {
                return JSON.parse(data);
            } catch {
                return [];
            }
        }
        return Array.isArray(data) ? data : [];
    }

    /**
     * Show-wide completion journal for the "Historie" view: every completion
     * newest-first, one row per task per day, paged. Rows share the shape in
     * `completion-cadence.ts` (plus `taskId`/`taskName`), so the cadence badge
     * helper can be reused as-is. The backend returns a parsed list (not a
     * JSON string like the per-task history), so no parsing is needed here.
     */
    public async getCompletionJournal(limit: number = 50, offset: number = 0): Promise<any[]> {
        const showId = await this.showService.getActiveShowId();
        const data: any = await this.api.getAll<any>(
            `maintenance/completions?showId=${ showId }&limit=${ limit }&offset=${ offset }`
        );
        return Array.isArray(data) ? data : [];
    }

    public async getAllMaintenanceTaskCategories(): Promise<MaintenanceTaskCategory[]> {
        const showId = await this.showService.getActiveShowId();

        const response = await this.api.getAll<MaintenanceTaskCategory>(`maintenance/maintenancetaskcategories?showId=${ showId }`);
        return response.map(taskData =>
            Object.assign(new MaintenanceTaskCategory(), taskData)
        );
    }

    public async createMaintenanceTaskCategory(name: string, description: string, pinned: boolean = false): Promise<MaintenanceTaskCategory> {
        const showId = await this.showService.getActiveShowId();
        const createdById = (await this.userService.getActiveUserId()) ?? null;
        const payload = { name, description, pinned, showId, createdById };
        const response = await this.api.post<MaintenanceTaskCategory>('maintenance/maintenancetaskcategories', payload);
        return Object.assign(new MaintenanceTaskCategory(), (Array.isArray(response) ? response[0] : response) ?? {});
    }

    public async updateMaintenanceTaskCategory(id: number, name: string, description: string, pinned: boolean = false): Promise<MaintenanceTaskCategory> {
        const payload = { name, description, pinned };
        const response = await this.api.put<MaintenanceTaskCategory>(`maintenance/maintenancetaskcategories/${ id }`, payload);
        return Object.assign(new MaintenanceTaskCategory(), (Array.isArray(response) ? response[0] : response) ?? {});
    }

    public async deleteMaintenanceTaskCategory(id: number): Promise<void> {
        await this.api.delete(`maintenance/maintenancetaskcategories/${ id }`);
    }

    public async createMaintenanceTask(
        maintenanceTaskDto: MaintenanceTaskDTO
    ): Promise<any> {
        maintenanceTaskDto.createdById =            (await this.userService.getActiveUserId()) ?? null;
        maintenanceTaskDto.showId = await this.showService.getActiveShowId();
        const response = await this.api.post<any>(
            'maintenance/maintenancetasks',
            maintenanceTaskDto
        );
        console.log(
            'APP [MaintenanceService] createMaintenanceTask() - response:',
            response
        );
        return response;
    }

    public async markTaskAsCompleted(
        completedTask: MaintenanceTaskCompletionDTO
    ): Promise<any> {
        return await this.api.post<any>(
            `maintenance/maintenancetasks/${ completedTask.taskId }/complete`,
            completedTask
        );
    }

    public async updateMaintenanceTask(maintenanceTaskDTO: MaintenanceTaskDTO, id: number): Promise<any> {
        return await this.api.put<any>(
            `maintenance/maintenancetasks/${ id }`,
            maintenanceTaskDTO
        );

    }

    private sortMaintenanceTasks(
        tasksToSort: MaintenanceTask[]
    ): MaintenanceTask[] {
        const maintenancetasksWithoutCompletion = tasksToSort.filter(
            task => task.lastCompletion === null
        );

        const maintenanceTasksWithCompletion = tasksToSort
            .filter(task => task.lastCompletion !== null)
            .sort((a, b) => a.daysUntilNextCompletion - b.daysUntilNextCompletion);

        tasksToSort = maintenancetasksWithoutCompletion.concat(
            maintenanceTasksWithCompletion
        );
        return tasksToSort;
    }
}
